import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Activity, ChevronDown, ChevronRight, User, Mail, Building2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLOR = {
  "Strong and Resilient": "text-[#4ADE80] border-[#4ADE80]/40",
  "Functional but Vulnerable": "text-[#D8A936] border-[#D8A936]/40",
  "Highly Person-Dependent": "text-[#F97316] border-[#F97316]/40",
  "Immediate Operational Risk": "text-red-400 border-red-500/40",
};

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminDiagnostics() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/diagnostics`, { withCredentials: true });
      setItems(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendDigest = async () => {
    if (sendingDigest) return;
    setSendingDigest(true);
    try {
      const r = await axios.post(`${API}/admin/diagnostics/digest?days=7`, {}, { withCredentials: true });
      toast.success(`Digest queued — ${r.data.count} submissions from the last ${r.data.days} days sent to ${r.data.recipients?.join(", ") || "admin"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send digest");
    } finally {
      setSendingDigest(false);
    }
  };

  const tierCount = items.reduce((acc, i) => {
    acc[i.tier] = (acc[i.tier] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="admin-diagnostics">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Signal</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-tight tracking-tight">Diagnostics</h1>
          <p className="mt-3 text-[#A9A9A6] text-sm max-w-lg">
            Every submission of the Program Resilience Diagnostic. Great signal for who is self-identifying as needing help — and how urgently.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-[#A9A9A6]" data-testid="diagnostics-total">{items.length} total</span>
          <button
            onClick={sendDigest}
            disabled={sendingDigest}
            data-testid="diagnostics-send-digest"
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={14} /> {sendingDigest ? "Sending…" : "Send weekly digest"}
          </button>
        </div>
      </div>

      {/* Tier breakdown */}
      {items.length > 0 && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#343437] border border-[#343437]" data-testid="diagnostics-tier-breakdown">
          {["Strong and Resilient", "Functional but Vulnerable", "Highly Person-Dependent", "Immediate Operational Risk"].map((t) => (
            <div key={t} className="bg-[#0B0B0C] p-5" data-testid={`tier-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6] leading-tight">{t}</p>
              <p className="mt-3 font-display text-3xl text-[#D8A936]">{tierCount[t] || 0}</p>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="mt-10 text-[#A9A9A6]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 border border-[#343437] p-10 text-center text-[#A9A9A6]" data-testid="diagnostics-empty">
          <Activity size={20} className="mx-auto text-[#D8A936] mb-3" />
          No diagnostic submissions yet. Once visitors complete the 12-question quiz at <span className="text-[#FAF9F6]">/diagnostic</span>, results land here.
        </div>
      ) : (
        <div className="mt-8 border border-[#343437]" data-testid="diagnostics-list">
          <div className="hidden md:grid grid-cols-[80px_1fr_260px_160px_120px] gap-0 border-b border-[#343437] bg-[#151517]">
            {["Score", "Contact", "Tier", "When", ""].map((h) => (
              <div key={h} className="px-4 py-3 text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">{h}</div>
            ))}
          </div>
          {items.map((d) => {
            const expanded = expandedId === d.id;
            const tierClass = TIER_COLOR[d.tier] || "text-[#A9A9A6] border-[#343437]";
            return (
              <div
                key={d.id}
                className="border-t border-[#343437] first:border-t-0"
                data-testid={`diagnostic-row-${d.id}`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : d.id)}
                  className="w-full grid grid-cols-1 md:grid-cols-[80px_1fr_260px_160px_120px] gap-4 md:gap-0 items-center text-left hover:bg-[#151517] transition-colors"
                  data-testid={`diagnostic-expand-${d.id}`}
                >
                  <div className="px-4 py-4 font-display text-lg text-[#D8A936]">
                    {d.score}<span className="text-xs text-[#A9A9A6]">/{d.max_score}</span>
                  </div>
                  <div className="px-4 py-4">
                    <p className="font-display text-[15px] text-[#FAF9F6] leading-tight">
                      {d.full_name || <span className="text-[#A9A9A6] italic">Anonymous</span>}
                    </p>
                    <p className="text-xs text-[#A9A9A6] mt-1 truncate">
                      {d.email || "no email"}{d.organisation ? ` · ${d.organisation}` : ""}
                    </p>
                  </div>
                  <div className="px-4 py-4">
                    <span className={`text-[10px] tracking-[0.18em] uppercase border px-2 py-1 ${tierClass}`}>
                      {d.tier}
                    </span>
                  </div>
                  <div className="px-4 py-4 text-xs text-[#A9A9A6]">{formatWhen(d.created_at)}</div>
                  <div className="px-4 py-4 flex items-center justify-end text-[#A9A9A6]">
                    {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </div>
                </button>

                {expanded && (
                  <div className="border-t border-[#343437] bg-[#151517] px-4 py-6 lg:px-8 lg:py-8" data-testid={`diagnostic-detail-${d.id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                      <DetailField icon={User} label="Name" value={d.full_name || "—"} />
                      <DetailField icon={Mail} label="Email" value={d.email || "—"} />
                      <DetailField icon={Building2} label="Organisation" value={d.organisation || "—"} />
                    </div>
                    <p className="eyebrow">Answers · 12 questions · 0-3 each</p>
                    <div className="mt-3 grid grid-cols-6 md:grid-cols-12 gap-2">
                      {(d.answers || []).map((a, i) => (
                        <div
                          key={`${d.id}-a-${i}`}
                          className="border border-[#343437] p-3 text-center bg-[#0B0B0C]"
                          data-testid={`diagnostic-answer-${d.id}-${i}`}
                        >
                          <p className="text-[9px] tracking-[0.2em] uppercase text-[#A9A9A6]">Q{i + 1}</p>
                          <p className="mt-1 font-display text-lg text-[#D8A936]">{a}</p>
                        </div>
                      ))}
                    </div>
                    {d.email && (
                      <div className="mt-8 pt-6 border-t border-[#343437]">
                        <a
                          href={`mailto:${d.email}?subject=Your%20Program%20Resilience%20result%20-%20${encodeURIComponent(d.tier)}&body=Hi%20${encodeURIComponent(d.full_name || "there")},%0D%0A%0D%0AThanks%20for%20taking%20the%20Program%20Resilience%20Diagnostic.%20`}
                          data-testid={`diagnostic-reply-${d.id}`}
                          className="btn-primary"
                        >
                          <Mail size={14} /> Reply to {d.full_name?.split(" ")[0] || "them"}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DetailField({ icon: Icon, label, value }) {
  return (
    <div>
      <p className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">
        <Icon size={12} className="text-[#D8A936]" /> {label}
      </p>
      <p className="mt-2 font-display text-[15px] text-[#FAF9F6] leading-snug break-words">{value}</p>
    </div>
  );
}
